<template>
  <div>
    <HrPageLayout
      :title="poll ? poll.title : 'Poll'"
      subtitle="Vote or view results"
      icon="📊"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'Polls', route: '/my-polls' },
        { label: poll ? poll.title : 'Vote' }
      ]"
    >
      <div class="poll-vote-content">
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading...</p>
        </div>

        <div v-else-if="submitted" class="poll-vote-card poll-vote-success">
          <i class="bx bx-check-circle" style="font-size: 48px; color: #10b981;"></i>
          <h5 class="hr-mt-3">Thank you</h5>
          <p class="hr-text-muted">Your response has been recorded anonymously.</p>
          <div class="poll-vote-actions">
            <button v-if="canViewResults" class="hr-btn hr-btn-primary" @click="loadResults">
              <i class="bx bx-bar-chart-alt"></i> View results
            </button>
            <router-link to="/my-polls" class="hr-btn hr-btn-secondary">Back to Polls</router-link>
          </div>
        </div>

        <div v-else-if="alreadyVoted && resultsLoaded" class="poll-vote-card">
          <h5 class="hr-mb-2">{{ poll.title }}</h5>
          <p class="hr-text-muted hr-small">You have already voted in this poll.</p>
          <div v-if="canViewResults" class="poll-results hr-mt-4">
            <h6 class="hr-mb-2">Results</h6>
            <p class="hr-text-muted hr-small hr-mb-3">Total votes: {{ resultsUniqueVoters }}</p>
            <div v-for="opt in resultsOptions" :key="opt.option_id" class="poll-result-item hr-mb-3">
              <div class="hr-flex-between hr-mb-1">
                <span>{{ opt.option_text }}</span>
                <span>{{ opt.vote_count }} vote(s)</span>
              </div>
              <div class="poll-progress-bar">
                <div class="poll-progress-fill" :style="{ width: (resultsUniqueVoters ? (opt.vote_count / resultsUniqueVoters * 100) : 0) + '%' }"></div>
              </div>
            </div>
          </div>
          <router-link to="/my-polls" class="hr-btn hr-btn-secondary hr-mt-4">Back to Polls</router-link>
        </div>

        <div v-else-if="poll" class="poll-vote-card">
          <h5 class="hr-mb-2">{{ poll.title }}</h5>
          <p v-if="poll.description" class="hr-text-muted hr-mb-2">{{ poll.description }}</p>
          <p class="hr-text-muted hr-small hr-mb-4">Ends: {{ formatDate(poll.end_date) }}</p>
          <form @submit.prevent="submitVote">
            <div class="hr-mb-4">
              <label class="hr-form-label">Select {{ poll.allow_multiple ? 'one or more' : 'one' }} option(s)</label>
              <div v-if="poll.allow_multiple" class="poll-options">
                <label v-for="opt in options" :key="opt.id" class="poll-option-item">
                  <input :id="'opt-' + opt.id" v-model="selectedIds" type="checkbox" :value="opt.id">
                  <span>{{ opt.option_text }}</span>
                </label>
              </div>
              <div v-else class="poll-options">
                <label v-for="opt in options" :key="opt.id" class="poll-option-item">
                  <input :id="'opt-' + opt.id" v-model="selectedIds" type="radio" :value="opt.id" name="pollOption">
                  <span>{{ opt.option_text }}</span>
                </label>
              </div>
            </div>
            <div class="poll-vote-actions">
              <button type="submit" class="hr-btn hr-btn-primary" :disabled="submitLoading || !selectedIds.length">
                <span v-if="submitLoading" class="hr-spinner small"></span>
                <i v-else class="bx bx-check"></i> Submit vote
              </button>
              <router-link to="/my-polls" class="hr-btn hr-btn-secondary">Cancel</router-link>
            </div>
          </form>
        </div>

        <div v-else class="hr-chart-card hr-text-center hr-py-5">
          <p class="hr-text-muted">Poll not found or not available.</p>
          <router-link to="/my-polls" class="hr-btn hr-btn-secondary hr-mt-3">Back to Polls</router-link>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "PollVote",
  components: { HrPageLayout },
  data() {
    return {
      loading: true,
      submitLoading: false,
      poll: null,
      options: [],
      selectedIds: [],
      alreadyVoted: false,
      submitted: false,
      resultsLoaded: false,
      resultsOptions: [],
      resultsUniqueVoters: 0,
      canViewResults: false
    };
  },
  computed: {
    pollId() {
      return parseInt(this.$route.params.id, 10) || 0;
    }
  },
  watch: {
    pollId: {
      immediate: true,
      handler() {
        if (this.pollId) this.loadPoll();
      }
    }
  },
  mounted() {
    if (this.pollId) this.loadPoll();
  },
  methods: {
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.toLocaleDateString();
    },
    loadPoll() {
      this.loading = true;
      this.poll = null;
      this.options = [];
      const data = new FormData();
      data.append("function", "getPollDetails");
      data.append("pollId", this.pollId);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data && res.data.data.poll) {
            this.poll = res.data.data.poll;
            this.options = res.data.data.options || [];
            this.canViewResults = this.poll.show_results_before_close === 1 || ["Closed", "Archived"].includes(this.poll.status);
            this.checkVoted();
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getSessionIdNo() {
      const direct = sessionStorage.getItem("idNo");
      if (direct) return direct;
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        return userInfo.idNo || "";
      } catch (e) {
        return "";
      }
    },
    getSessionNames() {
      const direct = sessionStorage.getItem("names");
      if (direct) return direct;
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        return userInfo.names || "";
      } catch (e) {
        return "";
      }
    },
    checkVoted() {
      const data = new FormData();
      data.append("function", "hasUserVoted");
      data.append("pollId", this.pollId);
      data.append("sessionIdNo", this.getSessionIdNo());
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data && res.data.data.voted) {
            this.alreadyVoted = true;
            if (this.canViewResults) this.loadResults();
          }
        })
        .catch(() => {});
    },
    submitVote() {
      const ids = Array.isArray(this.selectedIds) ? this.selectedIds : [this.selectedIds];
      const valid = ids.filter((id) => this.options.some((o) => o.id === id));
      if (this.poll.allow_multiple === 0 && valid.length !== 1) {
        toast.warning("Please select exactly one option.");
        return;
      }
      if (this.poll.allow_multiple !== 0 && valid.length === 0) {
        toast.warning("Please select at least one option.");
        return;
      }
      this.submitLoading = true;
      const data = new FormData();
      data.append("function", "submitPollVote");
      data.append("pollId", this.pollId);
      data.append("optionIds", JSON.stringify(valid));
      data.append("sessionIdNo", this.getSessionIdNo());
      data.append("sessionNames", this.getSessionNames());
      execute(data, authUrl)
        .then((res) => {
          this.submitLoading = false;
          if (res.data.success) {
            this.submitted = true;
            toast.success(res.data.message || res.data.data?.message || "Thank you, your response has been recorded.");
          } else {
            toast.error(res.data.message || "Failed to submit.");
          }
        })
        .catch(() => {
          this.submitLoading = false;
          toast.error("Request failed.");
        });
    },
    loadResults() {
      const data = new FormData();
      data.append("function", "getPollResults");
      data.append("pollId", this.pollId);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data) {
            this.resultsOptions = res.data.data.options || [];
            this.resultsUniqueVoters = res.data.data.uniqueVoters || 0;
            this.resultsLoaded = true;
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.poll-vote-content { max-width: 640px; margin: 0 auto; }
.poll-vote-card { padding: 32px; border: 1px solid #e2e8f0; border-radius: 16px; background: #fff; }
.poll-vote-success { text-align: center; border-color: #10b981; background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%); }
.poll-vote-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.poll-options { display: flex; flex-direction: column; gap: 12px; }
.poll-option-item { display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; }
.poll-option-item:hover { background: #f8fafc; }
.poll-option-item input { width: 18px; height: 18px; }
.poll-result-item { padding: 8px 0; }
.poll-progress-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.poll-progress-fill { height: 100%; background: linear-gradient(90deg, #5b73e8, #7c8ff0); transition: width 0.3s; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
</style>
